/**
 * Pure reducer for the Resend / cooldown state machine (Requirement 11).
 *
 * Exported independently of any React component so it can be property-tested
 * (see design.md "Property 7: The resend/cooldown state machine preserves its
 * disabled invariant"). Keep this module free of React / side-effects.
 *
 * State shape:
 *   {
 *     remainingSeconds: number   // countdown; 0 = idle
 *     attemptCount: number       // resends issued this session; cap = RESEND_ATTEMPT_CAP
 *     disabled: boolean          // derived: remainingSeconds > 0 || attemptCount >= cap
 *   }
 *
 * Actions:
 *   { type: 'requestResend' }  only takes effect when !disabled; sets
 *                              remainingSeconds = RESEND_COOLDOWN_SECONDS and
 *                              increments attemptCount (Req 11.1, 11.2).
 *   { type: 'tick' }           1Hz decrement of remainingSeconds toward 0; when it
 *                              reaches 0 the control re-enables unless the attempt
 *                              cap has been reached (Req 11.3).
 *   { type: 'resendFailed' }   resets remainingSeconds = 0 and re-enables unless the
 *                              attempt cap has been reached (Req 11.4).
 *
 * Once attemptCount reaches RESEND_ATTEMPT_CAP the control stays permanently
 * disabled for the remainder of the session (Req 11.5).
 *
 * NOTE on cooldown duration (60s vs 30s):
 *   Requirement 2.7 (the binding email-verification acceptance criterion) and the
 *   task text specify a 60-second cooldown, so RESEND_COOLDOWN_SECONDS is 60.
 *   The design.md ResendState prose and Req 11.1 mention a 30-second display in
 *   one place; that is treated as non-authoritative here. If a later reviewer
 *   decides 30s is correct, change the single constant below.
 */

export const RESEND_COOLDOWN_SECONDS = 60;
export const RESEND_ATTEMPT_CAP = 5;

export const RESEND_ACTIONS = {
  REQUEST_RESEND: 'requestResend',
  TICK: 'tick',
  RESEND_FAILED: 'resendFailed',
};

/**
 * Derives the disabled flag from the mutable fields so it can never drift out of
 * sync with remainingSeconds / attemptCount.
 * @param {number} remainingSeconds
 * @param {number} attemptCount
 * @returns {boolean}
 */
export const isResendDisabled = (remainingSeconds, attemptCount) => (
  remainingSeconds > 0 || attemptCount >= RESEND_ATTEMPT_CAP
);

export const initialResendState = {
  remainingSeconds: 0,
  attemptCount: 0,
  disabled: false,
};

/**
 * Pure resend/cooldown reducer.
 * @param {{ remainingSeconds: number, attemptCount: number, disabled: boolean }} state
 * @param {{ type: string }} action
 * @returns {{ remainingSeconds: number, attemptCount: number, disabled: boolean }}
 */
export const resendReducer = (state, action) => {
  switch (action?.type) {
    case RESEND_ACTIONS.REQUEST_RESEND: {
      // Reject requests while disabled (cooldown active or attempt cap reached).
      if (state.disabled) return state;
      const attemptCount = state.attemptCount + 1;
      const remainingSeconds = RESEND_COOLDOWN_SECONDS;
      return {
        remainingSeconds,
        attemptCount,
        disabled: isResendDisabled(remainingSeconds, attemptCount),
      };
    }

    case RESEND_ACTIONS.TICK: {
      // Never tick below zero.
      const remainingSeconds = Math.max(0, state.remainingSeconds - 1);
      return {
        remainingSeconds,
        attemptCount: state.attemptCount,
        disabled: isResendDisabled(remainingSeconds, state.attemptCount),
      };
    }

    case RESEND_ACTIONS.RESEND_FAILED: {
      const remainingSeconds = 0;
      return {
        remainingSeconds,
        attemptCount: state.attemptCount,
        disabled: isResendDisabled(remainingSeconds, state.attemptCount),
      };
    }

    default:
      return state;
  }
};

export default resendReducer;
